import { Alert, Box, Container, Paper, Stack, ThemeProvider, Typography } from '@mui/material';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CustomerDetailActivitiesSection from './customerDetail/CustomerDetailActivitiesSection';
import CustomerDetailBlockedState from './customerDetail/CustomerDetailBlockedState';
import CustomerDetailContactsSection from './customerDetail/CustomerDetailContactsSection';
import CustomerDetailFortnoxCard from './customerDetail/CustomerDetailFortnoxCard';
import CustomerDetailHeader from './customerDetail/CustomerDetailHeader';
import CustomerDetailOverviewCard from './customerDetail/CustomerDetailOverviewCard';
import CustomerDetailProfileSection from './customerDetail/CustomerDetailProfileSection';
import CustomerDetailTimelineSection from './customerDetail/CustomerDetailTimelineSection';
import {
  CRM_THEME,
  DETAIL_LAYOUT_SX,
  PAGE_BACKGROUND,
  SURFACE_SX,
  formatValue,
} from './customerDetail/customerDetailUtils';
import useCustomerDetailController from './customerDetail/useCustomerDetailController';

export default function CustomerDetailPage({ connected, setConnected, setError }) {
  const { customerNumber = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const controller = useCustomerDetailController({
    connected,
    customerNumber,
    navigate,
    setConnected,
    setError,
  });
  const backTo =
    typeof location.state?.backTo === 'string' ? location.state.backTo : '/customers';
  const backLabel =
    typeof location.state?.backLabel === 'string'
      ? location.state.backLabel
      : 'Back to Customers';

  return (
    <ThemeProvider theme={CRM_THEME}>
      <Box sx={{ background: PAGE_BACKGROUND, minHeight: '100vh', py: { xs: 3, md: 4 } }}>
        <Container maxWidth="xl">
          <Stack spacing={2.25}>
            <CustomerDetailHeader backLabel={backLabel} backTo={backTo} pageTitle={controller.pageTitle} />

            <CustomerDetailOverviewCard
              disabled={!connected}
              openActivityCount={controller.overview.openActivityCount}
              nextActivity={controller.overview.nextActivity}
              primaryContact={controller.overview.primaryContact}
              priorityLabel={controller.overview.priorityLabel}
              segmentLabel={controller.overview.segmentLabel}
              onAddActivity={controller.activities.onStartCreate}
              onAddContact={controller.contacts.onStartCreate}
            />

            {!connected ? (
              <CustomerDetailBlockedState customerNumber={customerNumber} />
            ) : (
              <>
                {controller.isLoading && (
                  <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading customer details, contacts, and activities...
                    </Typography>
                  </Paper>
                )}

                {controller.loadError && !controller.isLoading && (
                  <Alert severity="error">{controller.loadError}</Alert>
                )}

                {controller.notFound && !controller.isLoading && (
                  <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.5 } }}>
                    <Stack spacing={1}>
                      <Typography variant="h6" fontWeight={700}>
                        Customer not found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The customer number {formatValue(customerNumber)} does not exist in the
                        current tenant&apos;s CRM directory.
                      </Typography>
                    </Stack>
                  </Paper>
                )}

                {!controller.isLoading &&
                  !controller.loadError &&
                  !controller.notFound &&
                  controller.customer && (
                    <Box sx={DETAIL_LAYOUT_SX}>
                      <Stack spacing={2}>
                        <CustomerDetailActivitiesSection {...controller.activities} />
                        <CustomerDetailContactsSection {...controller.contacts} />
                        <CustomerDetailProfileSection {...controller.profile} />
                      </Stack>

                      <Stack spacing={2}>
                        <CustomerDetailTimelineSection {...controller.timeline} />
                        <CustomerDetailFortnoxCard customer={controller.customer} />
                      </Stack>
                    </Box>
                  )}
              </>
            )}
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
